// composeInviteEmail — pure builder for the tenant-invite email (S-295).
//
// Lives in core (not the adapter) so the content is domain-owned and
// unit-testable without a mail provider. The adapter just delivers the
// returned EmailMessage. HTML uses inline styles only — email clients
// ignore <style>/external CSS, and the design-system tokens (a web-app
// concern) don't apply here.
const ROLE_LABEL = {
    tenant_admin: "Workspace Admin",
    tenant_user: "Member",
};
const PRODUCT = "ContractStudio AI";
function escapeHtml(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
export function composeInviteEmail(input) {
    const role = ROLE_LABEL[input.role];
    const workspace = input.workspaceLabel;
    const expires = input.expiresAt.toUTCString();
    const subject = `You're invited to ${PRODUCT}`;
    const text = [
        `You've been invited to ${PRODUCT} as ${role} of the "${workspace}" workspace.`,
        "",
        "Set your password and sign in using this one-time link:",
        input.inviteLink,
        "",
        `This link expires ${expires}. If it lapses, ask your administrator to re-send the invite.`,
        "",
        `If you weren't expecting this, you can ignore this email.`,
    ].join("\n");
    const safeLink = escapeHtml(input.inviteLink);
    const html = `<!doctype html><html><body style="margin:0;padding:0;background:#f5f6f8;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f5f6f8;padding:32px 0;">
    <tr><td align="center">
      <table role="presentation" width="480" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;padding:32px;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1a1a2e;">
        <tr><td style="font-size:18px;font-weight:700;padding-bottom:8px;">${escapeHtml(PRODUCT)}</td></tr>
        <tr><td style="font-size:15px;line-height:1.5;padding-bottom:20px;">
          You've been invited as <strong>${escapeHtml(role)}</strong> of the
          <strong>${escapeHtml(workspace)}</strong> workspace. Set your password to get started.
        </td></tr>
        <tr><td style="padding-bottom:20px;">
          <a href="${safeLink}" style="display:inline-block;background:#3b1fb3;color:#ffffff;text-decoration:none;font-size:15px;font-weight:600;padding:12px 24px;border-radius:8px;">Set password &amp; sign in</a>
        </td></tr>
        <tr><td style="font-size:12px;line-height:1.5;color:#6b6b80;">
          This link expires ${escapeHtml(expires)}. If it lapses, ask your administrator to re-send the invite.
          If you weren't expecting this, you can ignore this email.
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`;
    return { to: input.to, subject, html, text };
}
// [S-616] composeResetEmail — the self-serve / admin-initiated password-reset
// email. Same one-time link mechanism as the invite (a `/set-password?token=…`
// URL), but reset copy ("you or an admin requested a reset") instead of
// "you've been invited". Pure builder, mirrors composeInviteEmail.
export function composeResetEmail(input) {
    const expires = input.expiresAt.toUTCString();
    const workspace = input.workspaceLabel;
    const subject = `Reset your ${PRODUCT} password`;
    const text = [
        `A password reset was requested for your ${PRODUCT} account${workspace !== undefined ? ` (${workspace} workspace)` : ""}.`,
        "",
        "Set a new password using this one-time link:",
        input.resetLink,
        "",
        `This link expires ${expires}. If you didn't request this, you can ignore this email — your password won't change.`,
    ].join("\n");
    const safeLink = escapeHtml(input.resetLink);
    const html = `<!doctype html><html><body style="margin:0;padding:0;background:#f5f6f8;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f5f6f8;padding:32px 0;">
    <tr><td align="center">
      <table role="presentation" width="480" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;padding:32px;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1a1a2e;">
        <tr><td style="font-size:18px;font-weight:700;padding-bottom:8px;">${escapeHtml(PRODUCT)}</td></tr>
        <tr><td style="font-size:15px;line-height:1.5;padding-bottom:20px;">
          A password reset was requested for your account. Set a new password to sign back in.
        </td></tr>
        <tr><td style="padding-bottom:20px;">
          <a href="${safeLink}" style="display:inline-block;background:#3b1fb3;color:#ffffff;text-decoration:none;font-size:15px;font-weight:600;padding:12px 24px;border-radius:8px;">Set a new password</a>
        </td></tr>
        <tr><td style="font-size:12px;line-height:1.5;color:#6b6b80;">
          This link expires ${escapeHtml(expires)}. If you didn't request this, you can ignore this email — your password won't change.
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`;
    return { to: input.to, subject, html, text };
}
//# sourceMappingURL=inviteEmail.js.map